import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { FiArrowRight } from 'react-icons/fi';
import { motion, AnimatePresence } from 'framer-motion';
import { fetchSpeeches } from '../apiService';
import { useLanguage } from '../utils/LanguageContext';

const SpeechesSection = () => {
  const [speeches, setSpeeches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedSpeech, setExpandedSpeech] = useState(null);
  const { language } = useLanguage();

  useEffect(() => {
    const loadLatestSpeeches = async () => {
      try {
        setLoading(true);
        const speechesData = await fetchSpeeches();
        setSpeeches(speechesData.slice(0, 3));
      } catch (err) {
        console.error('Error loading speeches:', err);
        setError(language === 'MR' ? 'भाषणे लोड करण्यात अयशस्वी' : 'Failed to load speeches');
      } finally {
        setLoading(false);
      }
    };

    loadLatestSpeeches();
  }, [language]);

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const options = { month: 'short', day: 'numeric', year: 'numeric' };
    return date.toLocaleDateString('en-US', options).toUpperCase();
  };

  const getImageSrc = (thumbnail) => {
    if (!thumbnail) return '/api/placeholder/600/400';
    if (thumbnail.startsWith('data:')) return thumbnail;
    if (thumbnail.startsWith('http')) return thumbnail;
    if (thumbnail.startsWith('uploads/')) {
      return `${process.env.NODE_ENV === 'production' ? '' : 'http://localhost:5500'}/${thumbnail}`;
    }
    return '/api/placeholder/600/400';
  };

  if (loading) {
    return (
      <section className="py-4 bg-white">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="flex items-center mb-10">
            <motion.div
              initial={{ opacity: 0, scaleX: 0 }}
              animate={{ opacity: 1, scaleX: 1 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              className="flex-1 h-0.5 bg-gradient-to-r from-transparent to-red-800"
            />
            <div className="text-center">
              <motion.h2 
                className="text-3xl font-bold text-red-800 mr-6"
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.5 }}
              >
                {language === 'MR' ? 'विचारांचा प्रतिध्वनी' : 'RESONATING WISDOM'}
              </motion.h2>
              <p className="text-sm text-gray-600 mt-2 italic">
                {language === 'MR' ? 'प्रभावी भाषणे आणि विचारसंवाद' : 'Powerful speeches and thought-provoking talks'}
              </p>
            </div>
            <motion.div
              initial={{ opacity: 0, scaleX: 0 }}
              animate={{ opacity: 1, scaleX: 1 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              className="flex-1 h-0.5 bg-gradient-to-r from-red-800 to-transparent"
            />
          </div>
          <div className="max-w-5xl mx-auto">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-10">
              {[...Array(3)].map((_, index) => (
                <div key={index} className="animate-pulse">
                  <div className="bg-gray-300 rounded-xl h-60 w-full mb-4"></div>
                  <div className="h-6 bg-gray-300 mb-2 w-3/4"></div>
                  <div className="h-4 bg-gray-300 w-1/2"></div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>
    );
  }

  if (error) {
    return (
      <section className="py-4 bg-white">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="flex items-center mb-10">
            <motion.div
              initial={{ opacity: 0, scaleX: 0 }}
              animate={{ opacity: 1, scaleX: 1 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              className="flex-1 h-0.5 bg-gradient-to-r from-transparent to-red-800"
            />
            <div className="text-center">
              <motion.h2 
                className="text-3xl font-bold text-red-800 mr-6"
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.5 }}
              >
                {language === 'MR' ? 'विचारांचा प्रतिध्वनी' : 'RESONATING WISDOM'}
              </motion.h2>
              <p className="text-sm text-gray-600 mt-2 italic">
                {language === 'MR' ? 'प्रभावी भाषणे आणि विचारसंवाद' : 'Powerful speeches and thought-provoking talks'}
              </p>
            </div>
            <motion.div
              initial={{ opacity: 0, scaleX: 0 }}
              animate={{ opacity: 1, scaleX: 1 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              className="flex-1 h-0.5 bg-gradient-to-r from-red-800 to-transparent"
            />
          </div>
          <div className="bg-red-50 border-2 border-red-100 rounded-xl p-6 max-w-md mx-auto">
            <div className="text-4xl text-red-800 mb-3">⚠</div>
            <p className="text-black font-semibold text-sm">{error}</p>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="py-4 bg-white">
      <div className="max-w-7xl mx-auto px-4 md:px-6">
        <div className="flex items-center mb-10">
          <motion.div
            initial={{ opacity: 0, scaleX: 0 }}
            animate={{ opacity: 1, scaleX: 1 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="flex-1 h-0.5 bg-gradient-to-r from-transparent to-red-800"
          />
          <div className="text-center">
            <motion.h2 
              className="text-3xl font-bold text-red-800 mr-6"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5 }}
            >
              {language === 'MR' ? 'विचारांचा प्रतिध्वनी' : 'RESONATING WISDOM'}
            </motion.h2>
            <p className="text-sm text-gray-600 mt-2 italic">
              {language === 'MR' ? 'प्रभावी भाषणे आणि विचारसंवाद' : 'Powerful speeches and thought-provoking talks'}
            </p>
          </div>
          <motion.div
            initial={{ opacity: 0, scaleX: 0 }}
            animate={{ opacity: 1, scaleX: 1 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="flex-1 h-0.5 bg-gradient-to-r from-red-800 to-transparent"
          />
        </div>

        <div className="max-w-5xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-10">
            {speeches.map((speech) => (
              <motion.div
                key={speech._id}
                className="relative cursor-pointer h-full rounded-xl overflow-hidden group"
                whileHover={{ 
                  scale: 1.03,
                  zIndex: 20,
                  transition: { duration: 0.3 }
                }}
                layout
              >
                <div 
                  className="h-full flex flex-col rounded-xl overflow-hidden shadow-lg bg-white"
                  onClick={() => setExpandedSpeech(speech)}
                >
                  {/* Image Section */}
                  <div className="relative w-full h-60 overflow-hidden">
                    <img loading="lazy" 
                      src={getImageSrc(speech.thumbnail)}
                      alt={speech.title}
                      className="w-full h-full object-cover transform group-hover:scale-105 transition-transform duration-500"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent"></div>
                    <div className="absolute top-3 right-3">
                      <span className="bg-white/90 text-black px-2 py-1 rounded-full text-xs font-bold tracking-wider">
                        {speech.topics?.[0]?.toUpperCase() || 'SPEECH'}
                      </span>
                    </div>
                  </div>

                  {/* Details Section */}
                  <div className="p-4 flex-1 flex flex-col justify-between">
                    <h3 className="text-lg font-bold text-black line-clamp-2 group-hover:text-red-800 transition-colors">
                      {speech.title}
                    </h3>
                    <div className="flex justify-between items-center mt-2">
                      <span className="text-gray-700 text-sm font-medium">
                        {formatDate(speech.date)}
                      </span>
                      <span className="text-red-800 font-bold text-sm flex items-center">
                        {language === 'MR' ? 'पहा' : 'VIEW'}
                        <FiArrowRight className="ml-1 group-hover:translate-x-1 transition-transform" />
                      </span>
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>

        {/* Expanded Speech Modal */}
        <AnimatePresence>
          {expandedSpeech && (
            <>
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="fixed inset-0 bg-black/70 z-50"
                onClick={() => setExpandedSpeech(null)}
              />
              
              <motion.div
                className="fixed inset-0 flex items-center justify-center z-[60] p-4"
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ 
                  opacity: 1, 
                  scale: 1,
                  transition: { 
                    type: "spring", 
                    damping: 25, 
                    stiffness: 300 
                  }
                }}
                exit={{ 
                  opacity: 0, 
                  scale: 0.8,
                  transition: { 
                    duration: 0.2 
                  } 
                }}
                onClick={(e) => e.stopPropagation()}
              >
                <motion.div 
                  className="w-full max-w-4xl bg-white rounded-xl shadow-2xl overflow-hidden border-2 border-amber-500"
                  layoutId={`speech-${expandedSpeech._id}`}
                >
                  <div className="flex flex-col md:flex-row h-full">
                    {/* Image Section - Larger */}
                    <div className="w-full md:w-1/2 h-96 relative">
                      <img loading="lazy" 
                        src={getImageSrc(expandedSpeech.thumbnail)}
                        alt={expandedSpeech.title}
                        className="w-full h-full object-cover"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent"></div>
                      <div className="absolute top-4 right-4">
                        <span className="bg-amber-500 text-white px-3 py-1.5 rounded-full text-xs font-bold tracking-wider">
                          {expandedSpeech.topics?.[0]?.toUpperCase() || 'SPEECH'}
                        </span>
                      </div>
                    </div>
                    
                    {/* Details Section - Enhanced */}
                    <div className="w-full md:w-1/2 p-6 flex flex-col h-full">
                      <div className="flex-grow">
                        <motion.h3 
                          className="text-2xl font-bold text-black mb-3"
                          initial={{ opacity: 0, y: 20 }}
                          animate={{ 
                            opacity: 1, 
                            y: 0,
                            transition: { delay: 0.2 }
                          }}
                        >
                          {expandedSpeech.title}
                        </motion.h3>
                        
                        <motion.p 
                          className="text-amber-700 text-sm mb-4 font-medium"
                          initial={{ opacity: 0 }}
                          animate={{ 
                            opacity: 1,
                            transition: { delay: 0.3 }
                          }}
                        >
                          {formatDate(expandedSpeech.date)}
                        </motion.p>
                        
                        <motion.p 
                          className="text-gray-700 mb-6 leading-relaxed"
                          initial={{ opacity: 0 }}
                          animate={{ 
                            opacity: 1,
                            transition: { delay: 0.4 }
                          }}
                        >
                          {expandedSpeech.description || (language === 'MR' 
                            ? 'ह्या भाषणाची संपूर्ण माहिती पाहण्यासाठी खालील बटणावर क्लिक करा.' 
                            : 'Click the button below to view full details of this speech.')}
                        </motion.p>
                      </div>
                      
                      <div className="flex justify-between items-center pt-4 border-t border-gray-200">
                        <motion.button
                          className="text-gray-500 hover:text-gray-700 transition-colors"
                          onClick={() => setExpandedSpeech(null)}
                          initial={{ opacity: 0 }}
                          animate={{ 
                            opacity: 1,
                            transition: { delay: 0.5 }
                          }}
                        >
                          {language === 'MR' ? 'बंद करा' : 'Close'}
                        </motion.button>
                        
                        <motion.div
                          initial={{ opacity: 0 }}
                          animate={{ 
                            opacity: 1,
                            transition: { delay: 0.6 }
                          }}
                        >
                          <Link 
                            to={`/speech/${expandedSpeech.slug || expandedSpeech._id}`}
                            className="px-5 py-2.5 rounded-full font-bold bg-gradient-to-r from-amber-500 to-amber-600 text-white hover:from-amber-600 hover:to-amber-700 transition-all duration-300 flex items-center gap-2"
                          >
                            {language === 'MR' ? 'पहा' : 'VIEW'}
                            <FiArrowRight className="w-4 h-4" />
                          </Link>
                        </motion.div>
                      </div>
                    </div>
                  </div>
                </motion.div>
              </motion.div>
            </>
          )}
        </AnimatePresence>
      </div>
    </section>
  );
};

export default SpeechesSection;